import {
    useCallback,
    forwardRef,
    useEffect,
    useState,
    useMemo,
    useRef,
    useId
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IItemProps from "./type";
import type {
    ItemRegisteredState,
    ItemRegisteredPart,
    ItemContextType,
    IItemRef
} from "./type";
import stylesheet, {
    ITEM_MEDIA_TOP_ALIGN_LINES,
    ITEM_DESCRIPTION_VARIANTS,
    getItemTitleVariant,
    getItemMeasures,
    getItemIconSize,
    getItemColors,
    getItemRadius,
    useStyles
} from "./stylesheet";
import ItemActions from "./components/actions";
import ItemContent from "./components/content";
import ItemDescription from "./components/description";
import ItemFooter from "./components/footer";
import ItemGroup from "./components/group";
import ItemHeader from "./components/header";
import ItemMedia from "./components/media";
import ItemSeparator from "./components/separator";
import ItemTitle from "./components/title";
import {
    useItemGroupContext,
    ItemContext
} from "./context";
import {
    getItemLayout
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    useReducedMotion
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    RefForwardingComponent
} from "../../types";
import {
    warnOnce
} from "../../utils/development";
import StateLayer from "../stateLayer";

const Item: RefForwardingComponent<IItemRef, IItemProps> = ({
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    "aria-haspopup": ariaHasPopup,
    "aria-selected": ariaSelected,
    customTheme: customThemeProp,
    "aria-checked": ariaChecked,
    "aria-pressed": ariaPressed,
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabled = false,
    isSelected = false,
    isLoading = false,
    variant = "plain",
    role: roleProp,
    size: sizeProp,
    borderRadius,
    onLongPress,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    children,
    onFocus,
    onPress,
    onBlur,
    style,
    ...props
}, ref) => {
    const groupContext = useItemGroupContext();

    const customTheme = customThemeProp ?? groupContext?.customTheme;

    const size = sizeProp ?? groupContext?.size ?? "medium";

    const {
        surfaceColor: parentSurfaceColor,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const [
        contentHeight,
        setContentHeight
    ] = useState<number | null>(null);

    const [
        registeredState,
        setRegisteredState
    ] = useState<ItemRegisteredState>({});

    const registeredRef = useRef<ItemRegisteredState>({});

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const isReduceMotionEnabled = useReducedMotion();

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onPressOut: isLoading ? undefined : onPressOut,
        onPressIn: isLoading ? undefined : onPressIn,
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const isWeb = Platform.OS === "web";

    const isInteractive = !!onPress || !!onLongPress;

    const isGroupedOutline = groupContext?.variant === "outline";

    const isSelectedVisible = isSelected && !isDisabled;

    const isStateFilled = isSelectedVisible || (isInteractive && !isLoading && (isHovered || isPressed));

    const currentLabel = ariaLabel ?? accessibilityLabel;

    const titleID = `${baseID}-title`;

    const descriptionID = `${baseID}-description`;

    const currentMeasures = getItemMeasures({
        controlHeights,
        touchTargets,
        pointerType,
        spaces,
        size
    });

    const currentRadius = getItemRadius({
        isGroupedOutline,
        borderRadius,
        radiuses,
        borders
    });

    const currentColors = getItemColors({
        isDisabled,
        isSelected,
        variant,
        colors
    });

    const iconSize = getItemIconSize({
        iconSizes,
        size
    });

    const titleVariant = getItemTitleVariant({
        isSelected: isSelectedVisible,
        size
    });

    const layout = getItemLayout({
        children
    });

    const {
        container: containerDynamicStyle,
        row: rowDynamicStyle
    } = useStyles({
        corners: {
            isFirst: groupContext?.isFirst ?? true,
            isLast: groupContext?.isLast ?? true
        },
        borderRadius: currentRadius,
        isReduceMotionEnabled,
        isGroupedOutline,
        currentMeasures,
        isFocusVisible,
        currentColors,
        isInteractive,
        shapeConfigs,
        durations,
        isLoading,
        variant,
        borders,
        colors
    });

    const registerPart = useCallback((part: ItemRegisteredPart, text?: string) => {
        registeredRef.current = {
            ...registeredRef.current,
            [part]: {
                text
            }
        };

        setRegisteredState(registeredRef.current);

        return () => {
            registeredRef.current = {
                ...registeredRef.current,
                [part]: undefined
            };

            setRegisteredState(registeredRef.current);
        };
    }, []);

    useEffect(() => {
        if(!isInteractive || isLoading || currentLabel || registeredRef.current.title) {
            return;
        }

        warnOnce({
            message: "Item: a pressable item needs an Item.Title (or an \"aria-label\" / \"accessibilityLabel\") so screen readers can name it."
        });
    }, [
        isInteractive,
        currentLabel,
        isLoading
    ]);

    const isMediaTopAligned = contentHeight !== null && contentHeight > (typography[titleVariant].lineHeight ?? 0) + (typography[ITEM_DESCRIPTION_VARIANTS[size]].lineHeight ?? 0) * ITEM_MEDIA_TOP_ALIGN_LINES;

    const context = useMemo<ItemContextType>(() => ({
        descriptionVariant: ITEM_DESCRIPTION_VARIANTS[size],
        descriptionColor: currentColors.descriptionColor,
        iconColorKey: currentColors.iconColorKey,
        titleColor: currentColors.titleColor,
        iconColor: currentColors.iconColor,
        onContentLayout: setContentHeight,
        isSelected: isSelectedVisible,
        isPressable: isInteractive,
        measures: currentMeasures,
        isMediaTopAligned,
        descriptionID,
        registerPart,
        titleVariant,
        customTheme,
        isDisabled,
        isLoading,
        iconSize,
        titleID,
        size
    }), [
        isMediaTopAligned,
        currentColors,
        currentMeasures,
        isSelectedVisible,
        descriptionID,
        isInteractive,
        registerPart,
        titleVariant,
        customTheme,
        isDisabled,
        isLoading,
        iconSize,
        titleID,
        size
    ]);

    const selectionProps = isWeb ? {
        "aria-current": isSelectedVisible && ariaSelected === undefined ? "page" as const : undefined,
        "aria-selected": ariaSelected
    } : {
        "aria-selected": ariaSelected ?? (isSelectedVisible ? true : undefined)
    };

    const stateProps = {
        "aria-controls": ariaControls,
        "aria-expanded": ariaExpanded,
        "aria-haspopup": ariaHasPopup,
        "aria-checked": ariaChecked,
        "aria-pressed": ariaPressed
    };

    const containerStateProps = isInteractive ? {} : stateProps;

    const groupRole = isWeb && groupContext ? "listitem" : undefined;

    const renderPressLayer = () => {
        if(!isInteractive) {
            return null;
        }

        const nameProps = isWeb ? {
            "aria-labelledby": !currentLabel && registeredState.title ? titleID : undefined,
            "aria-describedby": registeredState.description ? descriptionID : undefined
        } : {
            accessibilityHint: registeredState.description?.text
        };

        return <Pressable
            {...nameProps}
            {...interactionHandlers}
            {...selectionProps}
            {...stateProps}
            accessibilityLabel={currentLabel ?? (isWeb ? undefined : registeredState.title?.text)}
            onLongPress={isLoading ? undefined : onLongPress}
            onPress={isLoading ? undefined : onPress}
            role={roleProp ?? "button"}
            aria-disabled={isDisabled}
            style={stylesheet.layer}
            aria-busy={isLoading}
            disabled={isDisabled}
        >
            <StateLayer
                isHovered={!isLoading && isHovered}
                isPressed={!isLoading && isPressed}
                borderRadius={currentRadius}
                customTheme={customTheme}
                isSquircle={true}
            />
        </Pressable>;
    };

    return <ItemContext.Provider
        value={context}
    >
        <NCoreUIKitTheme.SurfaceProvider
            surfaceColor={isStateFilled ? "selected" : parentSurfaceColor}
        >
            <View
                {...props}
                {...containerStateProps}
                accessibilityLabel={isInteractive ? undefined : currentLabel}
                role={isInteractive ? groupRole : roleProp ?? groupRole}
                aria-label={isInteractive ? undefined : ariaLabel}
                aria-disabled={isDisabled ? true : undefined}
                aria-busy={isLoading || props["aria-busy"]}
                style={[
                    style,
                    stylesheet.container,
                    containerDynamicStyle
                ]}
                ref={ref}
            >
                {renderPressLayer()}
                {layout.headers}
                <View
                    style={[
                        stylesheet.row,
                        isInteractive ? stylesheet.passThrough : undefined,
                        rowDynamicStyle
                    ]}
                >
                    {layout.body}
                </View>
                {layout.footers}
            </View>
        </NCoreUIKitTheme.SurfaceProvider>
    </ItemContext.Provider>;
};
export default Object.assign(forwardRef(Item), {
    Description: ItemDescription,
    itemPart: "item" as const,
    Separator: ItemSeparator,
    Actions: ItemActions,
    Content: ItemContent,
    Footer: ItemFooter,
    Header: ItemHeader,
    Group: ItemGroup,
    Media: ItemMedia,
    Title: ItemTitle
});
export {
    getItemTitleVariant,
    getItemMeasures
};
